import { supabase } from '@/lib/supabase'
import {
  resolveAttachmentType, resetAfterSendFailure, resetAfterUploadFailure, sessionUsable, uploadFailureMessage,
  type TicketDraft,
} from '@/lib/supportTicketCore'

// Sends a ticket the person approved in Ask Rize's draft card — the ONLY caller of
// submit-support-ticket. The assistant itself can never reach it (spec:
// docs/superpowers/specs/2026-10-10-ask-rize-support-tickets-design.md).
//
// Attachments (screenshots or a screen recording, RIZ089) take HELLA Projects' own widget
// path: reserve signed upload URLs, PUT each file straight to HELLA Projects' storage, then
// file the ticket naming the upload session. Nothing large passes through our function.

const FALLBACK_ERROR = 'Couldn’t send the ticket. Try again in a minute.'
const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000

interface Upload {
  url: string
  type: string
  done: boolean
}

interface UploadSession {
  id: string
  uploads: Upload[]
  /** The exact files the session was prepared for; any change prepares a new one. */
  files: File[]
  preparedAt: number
}

class TicketSendError extends Error {
  status: number | null

  constructor(message: string, status: number | null) {
    super(message)
    this.status = status
  }
}

export interface SendTicketArgs {
  organizationId: string
  draft: TicketDraft
  files: File[]
  /** A progress line for the card: "Uploading 1 of 2: 40%", "Sending…". */
  onProgress?: (text: string) => void
}

/**
 * One sender per draft card. It keeps the upload session between attempts, the way HELLA
 * Projects' own widget does: a retry re-sends the ticket without re-uploading the files that
 * already went up, and re-sending a session HELLA Projects already turned into a ticket returns
 * that ticket instead of filing a second one.
 */
export function createTicketSender(): (args: SendTicketArgs) => Promise<{ reference: string }> {
  let session: UploadSession | null = null

  return async function send({ organizationId, draft, files, onProgress }) {
    if (files.length > 0) {
      let current = session
      if (!current || !sessionUsable(current, files, Date.now())) {
        onProgress?.('Preparing uploads…')
        current = await prepareUploads(organizationId, files)
        session = current
      }
      try {
        for (const [i, file] of files.entries()) {
          const upload = current.uploads[i]
          if (!upload) throw new TicketSendError(FALLBACK_ERROR, null)
          if (upload.done) continue
          await putFile(file, upload, (percent) =>
            onProgress?.(`Uploading ${i + 1} of ${files.length}${percent === null ? '…' : `: ${percent}%`}`))
          upload.done = true
        }
      } catch (err) {
        if (err instanceof TicketSendError && resetAfterUploadFailure(err.status)) session = null
        throw err
      }
    }
    onProgress?.('Sending…')
    try {
      return await sendTicket(organizationId, draft, files.length > 0 ? session?.id : undefined)
    } catch (err) {
      if (err instanceof TicketSendError && resetAfterSendFailure(err.status)) session = null
      throw err
    }
  }
}

async function prepareUploads(organizationId: string, files: File[]): Promise<UploadSession> {
  const { data, error } = await supabase.functions.invoke('submit-support-ticket', {
    body: {
      action: 'prepare_upload',
      organization_id: organizationId,
      files: files.map((f) => ({ name: f.name, type: resolveAttachmentType(f.type, f.name) ?? f.type, size: f.size })),
    },
  })
  if (error) throw await edgeError(error)
  const parsed = data as { upload_session?: unknown; uploads?: unknown } | null
  const sessionId = parsed?.upload_session
  const uploads = parsed?.uploads
  if (typeof sessionId !== 'string' || !Array.isArray(uploads) || uploads.length !== files.length) {
    throw new TicketSendError(FALLBACK_ERROR, null)
  }
  return {
    id: sessionId,
    uploads: uploads.map((u: { url?: unknown; type?: unknown }) => ({ url: String(u.url), type: String(u.type), done: false })),
    files: [...files],
    preparedAt: Date.now(),
  }
}

/** PUT one file to its signed URL with the type HELLA Projects resolved (its widget does the same). */
function putFile(file: File, upload: Upload, onPercent: (percent: number | null) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', upload.url)
    xhr.setRequestHeader('Content-Type', upload.type)
    xhr.timeout = UPLOAD_TIMEOUT_MS
    xhr.upload.onprogress = (event) => onPercent(event.lengthComputable ? Math.round((event.loaded / event.total) * 100) : null)
    xhr.onload = () => {
      // 409: the object is already there (an earlier try lost its response); HELLA Projects
      // checks its size and type when the ticket is filed.
      if ((xhr.status >= 200 && xhr.status < 300) || xhr.status === 409) resolve()
      else reject(new TicketSendError(uploadFailureMessage(file.name, xhr.status), xhr.status))
    }
    xhr.onerror = () => reject(new TicketSendError('The upload was interrupted. Check your connection and press Send to try again.', null))
    xhr.ontimeout = xhr.onerror
    xhr.send(file)
  })
}

async function sendTicket(organizationId: string, draft: TicketDraft, uploadSession: string | undefined): Promise<{ reference: string }> {
  const { data, error } = await supabase.functions.invoke('submit-support-ticket', {
    body: {
      organization_id: organizationId,
      type: draft.type,
      urgency: draft.urgency,
      title: draft.title,
      description: draft.description,
      // The page behind the panel; the server keeps origin + path only.
      page_url: window.location.origin + window.location.pathname,
      ...(uploadSession ? { upload_session: uploadSession } : {}),
    },
  })
  if (error) throw await edgeError(error)
  const reference = (data as { reference?: unknown } | null)?.reference
  if (typeof reference !== 'string' || !reference) throw new TicketSendError(FALLBACK_ERROR, null)
  return { reference }
}

// supabase-js rejects with a FunctionsHttpError whose real message is in the response body —
// `String(error)` renders "[object Object]" (CLAUDE.md gotcha). The status decides retries.
async function edgeError(error: unknown): Promise<TicketSendError> {
  const context = (error as { context?: unknown }).context
  if (context instanceof Response) {
    const body: unknown = await context.json().catch(() => null)
    const message = (body as { error?: unknown } | null)?.error
    return new TicketSendError(typeof message === 'string' && message ? message : FALLBACK_ERROR, context.status)
  }
  return new TicketSendError(FALLBACK_ERROR, null)
}
